import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, User, Building2, Mail, Phone, MapPin, Save, Loader2, LogOut } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import TwoFactorSettings from '../components/TwoFactorSettings';

function Profile({ user, setUser, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    full_name: '',
    phone: '',
    address: '',
    company_name: '',
    tax_id: '',
    contact_person: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        full_name: user.full_name || '',
        phone: user.phone || '',
        address: user.address || '',
        company_name: user.company_name || '',
        tax_id: user.tax_id || '',
        contact_person: user.contact_person || ''
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // If B2C and has first/last name, combine them
      const submitData = { ...formData };
      if (user.role === 'B2C' && formData.first_name && formData.last_name) {
        submitData.full_name = `${formData.first_name} ${formData.last_name}`.trim();
      }

      await axios.put(`${API}/user/profile`, submitData, {
        headers: getAuthHeaders()
      });
      
      // Update local user state
      setUser({ ...user, ...submitData });
      toast.success(t('profile.saved') || 'Профиль сохранён');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('profile.saveError') || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to={`/${currentLang}`} className="flex items-center">
            <img 
              src="/logo.jpg" 
              alt="Ainanan" 
              className="h-10 object-contain"
            />
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={onLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              {t('common.logout')}
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 max-w-3xl mx-auto w-full px-4 py-8">
        <Link to={`/${currentLang}`} className="inline-flex items-center text-stone-600 hover:text-stone-800 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('auth.backToCatalog')}
        </Link>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6">
          <Link to={`/${currentLang}/profile`}>
            <Button variant="default" className="bg-emerald-700 hover:bg-emerald-800">
              {t('profile.title') || 'Профиль'}
            </Button>
          </Link>
          <Link to={`/${currentLang}/settings`}>
            <Button variant="outline">
              {t('settings.title') || 'Настройки'}
            </Button>
          </Link>
          <Link to={`/${currentLang}/orders`}>
            <Button variant="outline">
              {t('common.myOrders')}
            </Button>
          </Link>
          <Link to={`/${currentLang}/addresses`}>
            <Button variant="outline">
              {t('common.myAddresses')}
            </Button>
          </Link>
          {user?.role === 'B2B' && (
            <Link to={`/${currentLang}/legal-entities`}>
              <Button variant="outline">{t('legalEntities.title') || 'Юрлица'}</Button>
            </Link>
          )}
          {user?.role === 'B2B' && (
            <Link to={`/${currentLang}/b2b/networks`}>
              <Button variant="outline" data-testid="my-networks-btn">Мои сети</Button>
            </Link>
          )}
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                {user.role === 'B2B' ? (
                  <Building2 className="w-6 h-6 text-emerald-700" />
                ) : (
                  <User className="w-6 h-6 text-emerald-700" />
                )}
              </div>
              <div>
                <CardTitle>{t('profile.title') || 'Профиль'}</CardTitle>
                <CardDescription>
                  {user.role === 'B2B' 
                    ? (t('profile.businessAccount') || 'Бизнес-аккаунт') 
                    : (t('profile.personalAccount') || 'Личный аккаунт')}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email (read-only) */}
              <div>
                <Label className="text-gray-700 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {t('auth.email')}
                </Label>
                <div className="mt-1 p-3 bg-gray-100 rounded-md text-gray-600 flex items-center gap-2">
                  {user.email}
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                    {t('profile.verified') || 'подтверждён'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('profile.emailCantChange') || 'Email нельзя изменить'}
                </p>
              </div>

              {/* B2C: First Name & Last Name */}
              {user.role === 'B2C' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name" className="text-gray-700 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {t('auth.firstName') || 'Имя'}
                    </Label>
                    <Input
                      id="first_name"
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="Иван"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="text-gray-700">
                      {t('auth.lastName') || 'Фамилия'}
                    </Label>
                    <Input
                      id="last_name"
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder="Иванов"
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              {/* B2B: Company Fields */}
              {user.role === 'B2B' && (
                <>
                  <div>
                    <Label htmlFor="company_name" className="text-gray-700 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      {t('auth.companyName')}
                    </Label>
                    <Input
                      id="company_name"
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      placeholder="ТОО Компания"
                      className="mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="tax_id" className="text-gray-700">
                        {t('auth.taxId')}
                      </Label>
                      <Input
                        id="tax_id"
                        type="text"
                        value={formData.tax_id}
                        onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                        placeholder="123456789012"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact_person" className="text-gray-700">
                        {t('auth.contactPerson')}
                      </Label>
                      <Input
                        id="contact_person"
                        type="text"
                        value={formData.contact_person}
                        onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                        placeholder="Иван Иванов"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="text-gray-700 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {t('auth.phone')}
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+7 777 123 4567"
                  className="mt-1"
                />
              </div>

              {/* Address */}
              <div>
                <Label htmlFor="address" className="text-gray-700 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {t('auth.deliveryAddress')}
                </Label>
                <Input
                  id="address"
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Город, улица, дом"
                  className="mt-1"
                />
              </div>

              {/* Account Info */}
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500">
                  {t('profile.memberSince') || 'Дата регистрации'}: {new Date(user.created_at).toLocaleDateString()}
                </p>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-700 hover:bg-emerald-800"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('profile.saving') || 'Сохранение...'}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {t('profile.save') || 'Сохранить изменения'}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <TwoFactorSettings user={user} />
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Ainanan. {t('common.allRights')}.
        </div>
      </footer>
    </div>
  );
}

export default Profile;
